import { createFileRoute, Link } from '@tanstack/react-router';
import { Button, Stack, Badge } from '@mantine/core';
import { IconDownload, IconPlus, IconUpload } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { MantineReactTable, useMantineReactTable, type MRT_ColumnDef, type MRT_ColumnFiltersState, type MRT_PaginationState, type MRT_SortingState } from 'mantine-react-table';
import { notifications } from '@mantine/notifications';
import { useJsonApiTable } from '@/shared/components/DataTable/useJsonApiTable';
import { PageHeader } from '@/shared/components/PageHeader';
import { fetchList } from '@/shared/api/jsonapi/client';
import { downloadCsv, type CsvColumn } from '@/shared/lib/exportCsv';
import type { FilterClause, ListQuery } from '@/shared/api/jsonapi/types';
import type { UserRow } from '@/features/users/api';

const DEFAULT_PAGE_SIZE = 25;
/** Sort di default (email ascendente): se attivo l'URL resta pulito. */
const DEFAULT_SORT = 'email';
/** Valore sentinella per "nessun ordinamento" (distinto dal default). */
const SORT_NONE = 'none';
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_ROWS = 2000;

/** Search params lista utenti: param assente = default (URL pulito). */
interface UsersSearch {
  /** 1-based; assente = pagina 1. */
  page?: number;
  /** Assente = 25. */
  pageSize?: number;
  /** Es. 'email' | '-lastName' | 'none'; assente = 'email' asc. */
  sort?: string;
  /** Ricerca globale (campo email). */
  q?: string;
  /** Column filters MRT: { colonna: valore }. */
  filters?: Record<string, string>;
}

function parseFiltersParam(v: unknown): Record<string, string> | undefined {
  if (v == null || typeof v !== 'object' || Array.isArray(v)) return undefined;
  const out: Record<string, string> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (typeof val === 'string' && val !== '') out[k] = val;
  }
  return Object.keys(out).length ? out : undefined;
}

export const Route = createFileRoute('/_app/users/')({
  validateSearch: (s: Record<string, unknown>): UsersSearch => ({
    page: typeof s.page === 'number' && s.page > 1 ? Math.floor(s.page) : undefined,
    pageSize: typeof s.pageSize === 'number' && s.pageSize > 0 && s.pageSize !== DEFAULT_PAGE_SIZE ? Math.floor(s.pageSize) : undefined,
    sort: typeof s.sort === 'string' && s.sort && s.sort !== DEFAULT_SORT ? s.sort : undefined,
    q: typeof s.q === 'string' && s.q ? s.q : undefined,
    filters: parseFiltersParam(s.filters),
  }),
  component: UsersListPage,
});

function sortParamToState(p?: string): MRT_SortingState {
  if (!p) return [{ id: DEFAULT_SORT, desc: false }];
  if (p === SORT_NONE) return [];
  return [{ id: p.replace(/^-/, ''), desc: p.startsWith('-') }];
}

function sortStateToParam(s: MRT_SortingState): string | undefined {
  if (s.length === 0) return SORT_NONE;
  const v = `${s[0].desc ? '-' : ''}${s[0].id}`;
  return v === DEFAULT_SORT ? undefined : v;
}

const USER_CSV_COLUMNS: Array<CsvColumn<UserRow>> = [
  { header: 'Email', accessor: (r) => r.email },
  { header: 'Nome', accessor: (r) => r.firstName },
  { header: 'Cognome', accessor: (r) => r.lastName },
  { header: 'Telefono', accessor: (r) => r.phoneNumber },
  { header: 'Confermato', accessor: (r) => (r.emailConfirmed == null ? '' : r.emailConfirmed ? 'Sì' : 'No') },
  { header: 'Bloccato', accessor: (r) => (r.lockoutEnabled == null ? '' : r.lockoutEnabled ? 'Sì' : 'No') },
];

function UsersListPage() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const [exporting, setExporting] = useState(false);

  // Stato tabella derivato dai search params (URL = fonte di verità).
  const pagination: MRT_PaginationState = {
    pageIndex: (search.page ?? 1) - 1,
    pageSize: search.pageSize ?? DEFAULT_PAGE_SIZE,
  };
  const sorting = useMemo(() => sortParamToState(search.sort), [search.sort]);
  const filters = useMemo<MRT_ColumnFiltersState>(
    () => Object.entries(search.filters ?? {}).map(([id, value]) => ({ id, value })),
    [search.filters],
  );
  const globalFilter = search.q ?? '';

  const updateSearch = (patch: Partial<UsersSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const { data, isLoading, isError } = useJsonApiTable<UserRow>('users', {
    pagination,
    sorting,
    filters,
    globalFilter,
    globalFilterFields: ['email'],
  });

  const exportCsv = async () => {
    setExporting(true);
    try {
      // Ricostruisce la stessa query della tabella (sort+filtri correnti) e
      // scarica tutte le pagine fino al cap.
      const filter: Record<string, FilterClause> = {};
      for (const f of filters) {
        if (f.value == null || f.value === '') continue;
        if (typeof f.value === 'string') filter[f.id] = { like: f.value };
        else if (typeof f.value === 'number' || typeof f.value === 'boolean') filter[f.id] = { eq: f.value };
      }
      if (globalFilter) filter.email = { like: globalFilter };
      const sort = sorting.map((s) => `${s.desc ? '-' : ''}${s.id}`);

      const rows: UserRow[] = [];
      for (let page = 1; rows.length < EXPORT_MAX_ROWS; page++) {
        const query: ListQuery = {
          page: { number: page, size: EXPORT_PAGE_SIZE },
          sort: sort.length ? sort : undefined,
          filter: Object.keys(filter).length ? filter : undefined,
        };
        const res = await fetchList<UserRow>('users', query);
        rows.push(...res.data);
        if (res.data.length < EXPORT_PAGE_SIZE) break;
      }
      const truncated = rows.length >= EXPORT_MAX_ROWS;
      downloadCsv(`utenti-${new Date().toISOString().slice(0, 10)}`, USER_CSV_COLUMNS, rows.slice(0, EXPORT_MAX_ROWS));
      notifications.show({
        color: truncated ? 'orange' : 'green',
        message: truncated
          ? `Esportate le prime ${EXPORT_MAX_ROWS} righe (il dataset è più grande: filtra per esportare il resto).`
          : `Esportate ${rows.length} righe.`,
      });
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Errore export' });
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<MRT_ColumnDef<UserRow>[]>(
    () => [
      { accessorKey: 'email', header: 'Email' },
      { accessorKey: 'firstName', header: 'Nome' },
      { accessorKey: 'lastName', header: 'Cognome' },
      { accessorKey: 'phoneNumber', header: 'Telefono', enableSorting: false },
      {
        accessorKey: 'emailConfirmed',
        header: 'Confermato',
        Cell: ({ cell }) =>
          cell.getValue<boolean>() ? <Badge color="green">Sì</Badge> : <Badge color="yellow">No</Badge>,
        size: 110,
      },
      {
        accessorKey: 'lockoutEnabled',
        header: 'Bloccato',
        Cell: ({ cell }) =>
          cell.getValue<boolean>() ? <Badge color="red">Sì</Badge> : <Badge color="gray">No</Badge>,
        size: 100,
      },
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data: data?.data ?? [],
    rowCount: data?.total ?? 0,
    state: {
      pagination,
      sorting,
      columnFilters: filters,
      globalFilter,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isLoading,
    },
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableRowSelection: true,
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater(pagination) : updater;
      updateSearch({
        page: next.pageIndex > 0 ? next.pageIndex + 1 : undefined,
        pageSize: next.pageSize !== DEFAULT_PAGE_SIZE ? next.pageSize : undefined,
      });
    },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      updateSearch({ sort: sortStateToParam(next), page: undefined });
    },
    onColumnFiltersChange: (updater) => {
      const next = typeof updater === 'function' ? updater(filters) : updater;
      const rec: Record<string, string> = {};
      for (const f of next) {
        if (typeof f.value === 'string' && f.value !== '') rec[f.id] = f.value;
        else if (typeof f.value === 'number' || typeof f.value === 'boolean') rec[f.id] = String(f.value);
      }
      updateSearch({ filters: Object.keys(rec).length ? rec : undefined, page: undefined });
    },
    onGlobalFilterChange: (v: string | undefined) => updateSearch({ q: v || undefined, page: undefined }),
    renderTopToolbarCustomActions: () => (
      <Button
        size="xs"
        variant="default"
        leftSection={<IconDownload size={14} />}
        loading={exporting}
        disabled={isLoading || (data?.total ?? 0) === 0}
        onClick={() => void exportCsv()}
      >
        Esporta CSV
      </Button>
    ),
    mantineToolbarAlertBannerProps: isError ? { color: 'red', children: 'Errore caricamento' } : undefined,
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => void navigate({ to: `/users/${row.original.id}` }),
      style: { cursor: 'pointer' },
    }),
    initialState: { showColumnFilters: false, density: 'xs' },
    paginationDisplayMode: 'pages',
    enableStickyHeader: true,
  });

  return (
    <Stack gap="lg">
      <PageHeader
        title="Utenti"
        description="Account, ruoli e permessi degli utenti del tenant."
        actions={
          <>
            <Button leftSection={<IconUpload size={16} />} variant="light" component={Link} to="/users/import">
              Importa Excel
            </Button>
            <Button leftSection={<IconPlus size={16} />} component={Link} to="/users/new">
              Nuovo utente
            </Button>
          </>
        }
      />
      <MantineReactTable table={table} />
    </Stack>
  );
}
